<template>
    <div class="dashboard-wrapper">
        <div ref="gridContainer" class="min-h-20" @contextmenu.prevent="openContextMenu($event, null, layout)">
            <draggable
                v-model="layout"
                :element="'div'"
                class="grid grid-cols-1 gap-2 lg:grid-cols-12"
                :disabled="!isEditable"
                item-key="id"
                @end="onDragEnd"
            >
                <template #item="{ element }">
                    <div
                        :key="element.id"
                        class="relative grid-item"
                        :class="['col-span-1', `lg:col-span-${element.w}`]"
                        @contextmenu.prevent="openContextMenu($event, element, layout)"
                    >
                        <div>
                            <component
                                :is="element.component"
                                class="dashboard-widget"
                                :settings="element.settings"
                                :slug="element.slug"
                                :data="element.data"
                            ></component>
                        </div>
                        <div
                            v-if="isEditable"
                            class="resize-handle"
                            @mousedown.prevent="initResize($event, element)"
                        ></div>
                    </div>
                </template>
            </draggable>
        </div>

        <!-- Modal for Adding Widget -->
        <Modal :show="showAddWidgetModal" @close="closeAddWidgetModal">
            <form @submit.prevent="createWidget">
                <h3>{{ lang('admin.dashboard.actions.add_widget') }}</h3>
                <AdminSelect v-model="creating" :options="widgets" :searchable="true"></AdminSelect>
                <Field v-for="setting in settings" :key="setting.slug" v-model="setting.value" :field="setting"></Field>
                <Button color="primary" type="submit">
                    {{ lang('admin.dashboard.actions.add_widget') }}
                </Button>
            </form>
        </Modal>

        <!-- Context Menu -->
        <UiContextMenu
            :items="contextMenuItems"
            :x="contextMenu.x"
            :y="contextMenu.y"
            :show="contextMenu.visible"
            @close="closeContextMenu"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useEventListener } from '@vueuse/core';
import draggable from 'vuedraggable';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { lang } from '@/lang/composable';
import type { ContextMenuItem } from '@src/Common/Components/UI/ContextMenu/ContextMenu.vue';

interface Props {
    slug?: string;
}

const props = withDefaults(defineProps<Props>(), {
    slug: null,
});

const form = reactive({
    slug: props.slug,
    config: {},
});

const loading = reactive({
    dashboard: false,
    saving: false,
    widgets: false,
});

const widgets = ref([]);

const layout = ref([]);

const gridContainer = ref(null);

const isEditable = ref(false);

const toggleEditMode = () => {
    isEditable.value = !isEditable.value;
};

// Drag-and-drop
const onDragEnd = () => {
    submit();
};

let resizingWidget = null;
let startX = 0;
let startWidth = 0;

const initResize = (event, widget) => {
    if (!isEditable.value) return;
    resizingWidget = widget;
    startX = event.clientX;
    startWidth = widget.w;
    useEventListener(window, 'mousemove', onMouseMove);
    useEventListener(window, 'mouseup', stopResize);
};

const onMouseMove = (event) => {
    if (!resizingWidget) return;
    const deltaX = event.clientX - startX;
    const containerWidth = gridContainer.value.offsetWidth;
    const columnWidth = containerWidth / 12;
    let newWidth = startWidth + Math.round(deltaX / columnWidth);
    newWidth = Math.max(1, Math.min(newWidth, 12));
    resizingWidget.w = newWidth;
};

const stopResize = () => {
    if (resizingWidget) {
        submit();
        resizingWidget = null;
    }
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', stopResize);
};

const contextMenu = reactive({
    visible: false,
    x: 0,
    y: 0,
    widget: null,
    layout: null,
});

const contextMenuItems = computed<ContextMenuItem[]>(() => [
    {
        label: isEditable.value
            ? lang('admin.dashboard.actions.stop_edit')
            : lang('admin.dashboard.actions.start_edit'),
        icon: 'hero/outline/arrows-right-left',
        action: toggleEditMode,
    },
    {
        label: lang('admin.dashboard.actions.add_widget'),
        icon: 'hero/outline/folder-plus',
        action: addWidget,
        show: !!contextMenu.layout,
    },
    {
        label: lang('admin.dashboard.actions.delete_widget'),
        icon: 'hero/outline/folder-minus',
        action: () => removeWidget(contextMenu.widget?.id),
        show: !!contextMenu.widget,
    },
]);

const openContextMenu = (event, widget = null, layout = null) => {
    event.preventDefault();
    event.stopPropagation();
    contextMenu.visible = true;
    contextMenu.x = event.clientX;
    contextMenu.y = event.clientY;
    contextMenu.widget = widget;
    contextMenu.layout = layout;
};

const closeContextMenu = () => {
    contextMenu.visible = false;
    contextMenu.widget = null;
    contextMenu.layout = null;
};

const showAddWidgetModal = ref(false);
const creating = ref(null);
const settings = ref(null);

const addWidget = () => {
    settings.value = null;
    showAddWidgetModal.value = true;
};

const closeAddWidgetModal = () => {
    showAddWidgetModal.value = false;
};

const createWidget = () => {
    const addingWidget = widgets.value.find((w) => w.slug === creating.value);
    const addingSettings = {};

    if (settings.value && settings.value.length > 0) {
        settings.value.forEach((setting) => {
            addingSettings[setting.slug] = setting.value;
        });
    }

    const newItem = {
        id: uuidv4(),
        w: 4,
        slug: addingWidget.slug,
        component: addingWidget.component,
        settings: addingSettings,
    };
    layout.value.push(newItem);

    closeAddWidgetModal();
    submit();
};

const removeWidget = (widgetId) => {
    if (!widgetId) return;
    layout.value = layout.value.filter((w) => w.id !== widgetId);
    submit();
};

const loadSettingsFromCreatingWidget = () => {
    if (creating.value) {
        const widget = widgets.value.find((w) => w.slug === creating.value);
        settings.value = widget.settings;
    }
};

watch(
    () => creating.value,
    (value) => {
        if (value) {
            loadSettingsFromCreatingWidget();
        }
    },
);

const submit = () => {
    loading.saving = true;

    form.config = layout.value;

    axios
        .post(route('admin.dashboards.save'), form)
        .then(({ data }) => {
            if (data.dashboard) {
                layout.value = data.dashboard.config;
            }
        })
        .finally(() => {
            loading.saving = false;
        });
};

const loadWidgets = () => {
    loading.widgets = true;

    axios
        .get(route('admin.dashboards.widgets.all'))
        .then(({ data }) => {
            widgets.value = data.options;
        })
        .finally(() => {
            loading.widgets = false;
        });
};

const load = () => {
    loading.dashboard = true;

    axios
        .get(route('admin.dashboards.get', { slug: form.slug }))
        .then(({ data }) => {
            if (data.dashboard) {
                layout.value = data.dashboard.config;
            }
        })
        .finally(() => {
            loading.dashboard = false;
        });
};

onMounted(() => {
    load();
    loadWidgets();
});

onBeforeUnmount(() => {
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', stopResize);
});
</script>

<style scoped>
.resize-handle {
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    width: 10px;
    cursor: ew-resize;
    background-color: rgba(0, 0, 0, 0.1);
    opacity: 0;
    pointer-events: none;
}

.resize-handle:hover {
    background-color: rgba(0, 0, 0, 0.2);
}

.grid-item:hover .resize-handle {
    opacity: 1;
    pointer-events: auto;
}
</style>
